import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import {
  createTJointGeometry,
  createTJointPlaneGeometry,
} from '@asset-viz/utils/geometryDefines'
import { extractScale } from '@asset-viz/utils/helpers'
import React from 'react'
import { GeckoPipeTJoint } from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A Rich Asset Model Pipe, plotted on a cartesian plane, with the center of its
 * base at "position" and decorated with Rich Asset features specific to pipes.
 * @param model               The pipe section component model
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */
export const PipeTJointSurface3D: React.FC<
  { model: GeckoPipeTJoint } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const { bounds, scale } = React.useMemo(() => {
    const scale = extractScale(groupProps.scale)

    return {
      bounds: model.componentBounds,
      scale: scale,
    }
  }, [model, groupProps.scale])

  const geometry = React.useMemo(() => {
    if (!show3D)
      return createTJointPlaneGeometry(
        model.chord_length.num,
        model.chord_radius.num,
        model.brace_length.num,
        model.brace_radius.num,
        model.componentBounds.end.x.num,
        model.componentBounds.end.y.num
      )

    return createTJointGeometry(
      model.chord_length.num,
      model.chord_radius.num,
      model.brace_length.num,
      model.brace_radius.num
    )
  }, [
    model.brace_length.num,
    model.brace_radius.num,
    model.chord_length.num,
    model.chord_radius.num,
    model.componentBounds.end.x.num,
    model.componentBounds.end.y.num,
    show3D,
  ])

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      scale={scale}></ModelSurface3D>
  )
}
